import { Link } from "react-router-dom";
import { ShieldCheck, Factory, Sparkles, ArrowRight, ShoppingBag, UtensilsCrossed, Layers } from "lucide-react";
import { SITE, ROUTES, PRODUCTS } from "../site";
import { usePageMeta } from "../hooks/usePageMeta";
import Header from "../components/Header";
import Footer from "../components/Footer";
import GummyBurger from "../components/GummyBurger";
import WavyDivider from "../components/WavyDivider";
import LayerWidget from "../components/LayerWidget";
import ProductCard from "../components/ProductCard";

const meta = ROUTES[0];

const TRUST = [
  {
    icon: ShieldCheck,
    title: "Halal MUI & BPJPH",
    text: "Produk Yupi tersertifikasi halal dan terdaftar di BPOM.",
    color: "text-lettuce-dark bg-lettuce-soft",
  },
  {
    icon: Factory,
    title: "Buatan Indonesia",
    text: "Diproduksi PT Yupi Indo Jelly Gum Tbk, berdiri sejak 1996.",
    color: "text-bun-dark bg-bun-soft",
  },
  {
    icon: Sparkles,
    title: "Ikon Permen Gummy",
    text: "Gummy berbentuk unik yang diekspor ke 40+ negara.",
    color: "text-patty-dark bg-patty-soft",
  },
];

export default function Home() {
  usePageMeta(meta.title, meta.description);

  return (
    <div data-testid="home-page">
      <Header />
      <main>
        <section className="gummy-speckles relative overflow-hidden bg-gradient-to-br from-amber-100 via-orange-50 to-pink-100">
          <div className="container-site grid grid-cols-1 items-center gap-10 py-14 sm:py-20 lg:grid-cols-2 lg:py-24">
            <div>
              <span className="eyebrow-chip" data-testid="hero-eyebrow">
                <Sparkles className="h-3.5 w-3.5 text-bun-dark" aria-hidden="true" />
                Permen Gummy Ikonik Indonesia
              </span>
              <h1 className="mt-5 text-balance font-display text-4xl font-extrabold leading-tight tracking-tight text-plum sm:text-5xl lg:text-6xl">
                Kenyalnya Seru, <span className="text-patty">Lapis Demi Lapis!</span>
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-plum-soft sm:text-lg">
                Yupi Burger adalah permen gummy berbentuk burger mini buatan Yupi — manis, kenyal,
                dan bisa dimakan lapis demi lapis. Camilan legendaris yang selalu bikin kangen.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/produk/" data-testid="hero-cta-explore-products" className="btn-primary">
                  Lihat Produk
                  <ArrowRight className="h-5 w-5" aria-hidden="true" />
                </Link>
                <a href="#lapis-widget" data-testid="hero-cta-interactive-layer" className="btn-secondary">
                  <Layers className="h-5 w-5" aria-hidden="true" />
                  Coba Lapis Demi Lapis
                </a>
              </div>
            </div>

            <div className="relative flex justify-center">
              <div
                aria-hidden="true"
                className="absolute left-6 top-8 h-10 w-10 animate-float-slow rounded-full bg-lettuce/50 blur-[1px]"
              />
              <div
                aria-hidden="true"
                className="absolute bottom-10 right-4 h-14 w-14 animate-float rounded-full bg-berry/50 blur-[1px]"
              />
              <div
                aria-hidden="true"
                className="absolute right-16 top-2 h-7 w-7 animate-bounce-slow rounded-full bg-cheese blur-[0.5px]"
              />
              <GummyBurger
                className="w-full max-w-md animate-float drop-shadow-2xl"
                title="Ilustrasi gummy Yupi Burger dengan lapisan roti, selada, keju, dan patty"
              />
            </div>
          </div>
          <WavyDivider fill="#FFFDF9" />
        </section>

        <section aria-label="Keunggulan produk" className="container-site -mt-2 py-10 sm:py-14">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            {TRUST.map((item) => (
              <div
                key={item.title}
                data-testid={`trust-${item.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="flex items-start gap-4 rounded-3xl border-2 border-plum/10 bg-white p-5 shadow-sm"
              >
                <span className={`rounded-2xl p-3 ${item.color}`}>
                  <item.icon className="h-6 w-6" aria-hidden="true" />
                </span>
                <div>
                  <h2 className="font-display text-base font-bold text-plum">{item.title}</h2>
                  <p className="mt-1 text-sm leading-relaxed text-plum-soft">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section id="lapis-widget" aria-labelledby="lapis-heading" className="bg-cheese-soft">
          <WavyDivider fill="#FFFDF9" flip />
          <div className="container-site py-14 sm:py-20">
            <div className="mx-auto max-w-2xl text-center">
              <span className="eyebrow-chip">Fitur Interaktif</span>
              <h2
                id="lapis-heading"
                className="mt-4 font-display text-2xl font-bold tracking-tight text-plum sm:text-3xl lg:text-4xl"
              >
                Coba Lepas, Lapis Demi Lapis!
              </h2>
              <p className="mt-3 text-base leading-relaxed text-plum-soft">
                Klik setiap lapisan gummy-nya dan kenali rasa serta fakta seru di baliknya — persis
                seperti cara paling seru makan Yupi Burger.
              </p>
            </div>
            <div className="mt-12">
              <LayerWidget />
            </div>
          </div>
          <WavyDivider fill="#FFFDF9" />
        </section>

        <section aria-labelledby="produk-heading" className="container-site py-14 sm:py-20">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <span className="eyebrow-chip">Varian Produk</span>
              <h2
                id="produk-heading"
                className="mt-4 font-display text-2xl font-bold tracking-tight text-plum sm:text-3xl lg:text-4xl"
              >
                Tiga Varian, Satu Kelezatan Kenyal
              </h2>
            </div>
            <Link
              to="/produk/"
              data-testid="home-cta-all-products"
              className="inline-flex items-center gap-2 font-display text-base font-bold text-patty transition-colors hover:text-patty-dark"
            >
              Lihat semua produk
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
            {PRODUCTS.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </section>

        <section aria-labelledby="cara-heading" className="bg-lettuce-soft">
          <WavyDivider fill="#FFFDF9" flip />
          <div className="container-site py-14 sm:py-20">
            <div className="mx-auto max-w-2xl text-center">
              <span className="eyebrow-chip">Cara Menikmati</span>
              <h2
                id="cara-heading"
                className="mt-4 font-display text-2xl font-bold tracking-tight text-plum sm:text-3xl lg:text-4xl"
              >
                Dua Cara Seru Menikmati Yupi Burger
              </h2>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-8">
              <div
                data-testid="enjoy-bite-direct"
                className="rounded-3xl border-2 border-plum/10 bg-white p-7 shadow-gummy sm:p-9"
              >
                <span className="inline-flex rounded-2xl bg-bun-soft p-3.5 text-bun-dark">
                  <UtensilsCrossed className="h-7 w-7" aria-hidden="true" />
                </span>
                <h3 className="mt-5 font-display text-xl font-bold text-plum sm:text-2xl">Gigit Langsung</h3>
                <p className="mt-3 text-sm leading-relaxed text-plum-soft sm:text-base">
                  Santap utuh seperti burger sungguhan. Semua lapisan rasa buah melebur jadi satu
                  dalam sekali gigit — praktis dan langsung puas.
                </p>
              </div>
              <div
                data-testid="enjoy-layer-by-layer"
                className="rounded-3xl border-2 border-plum/10 bg-white p-7 shadow-gummy sm:p-9"
              >
                <span className="inline-flex rounded-2xl bg-patty-soft p-3.5 text-patty-dark">
                  <Layers className="h-7 w-7" aria-hidden="true" />
                </span>
                <h3 className="mt-5 font-display text-xl font-bold text-plum sm:text-2xl">
                  Lepas Lapis Demi Lapis
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-plum-soft sm:text-base">
                  Kupas satu per satu: roti, selada, keju, sampai patty-nya. Setiap lapisan punya
                  rasa dan tekstur kenyal yang berbeda — makannya jadi lebih lama dan lebih seru.
                </p>
              </div>
            </div>
          </div>
          <WavyDivider fill="#FFFDF9" />
        </section>

        <section aria-labelledby="kangen-heading" className="container-site py-14 sm:py-20">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div className="order-2 flex justify-center lg:order-1">
              <GummyBurger
                variant="love"
                className="w-full max-w-xs animate-wiggle"
                title="Ilustrasi Yupi Gummy Love Burger"
              />
            </div>
            <div className="order-1 lg:order-2">
              <span className="eyebrow-chip">Permen Masa Kecil</span>
              <h2
                id="kangen-heading"
                className="mt-4 font-display text-2xl font-bold tracking-tight text-plum sm:text-3xl lg:text-4xl"
              >
                Camilan yang Bikin Kangen
              </h2>
              <p className="mt-4 text-base leading-relaxed text-plum-soft">
                Dari kantin sekolah sampai warung dekat rumah, Yupi Burger selalu jadi rebutan.
                Bentuknya lucu, rasanya manis, dan makannya seru — pantas saja gummy burger ini
                akrab dengan banyak generasi di Indonesia.
              </p>
              <Link to="/tentang/" data-testid="home-cta-about" className="btn-secondary mt-7">
                Kenali Ceritanya
                <ArrowRight className="h-5 w-5" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        <section aria-labelledby="cta-heading" className="bg-plum">
          <WavyDivider fill="#FFFDF9" flip />
          <div className="container-site py-16 text-center sm:py-20">
            <h2
              id="cta-heading"
              className="mx-auto max-w-2xl text-balance font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl"
            >
              Sudah Ngiler? Saatnya Borong Yupi Burger!
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-white/70">
              Temukan Yupi Burger di minimarket dan toko terdekat, atau belanja langsung di official
              store resmi Yupi.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a
                href={SITE.official.store}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-cta-buy-yupiland"
                className="btn-primary"
              >
                <ShoppingBag className="h-5 w-5" aria-hidden="true" />
                Beli di Yupiland Store
              </a>
              <Link to="/beli/" data-testid="home-cta-where-to-buy" className="btn-secondary !border-white/25 !bg-transparent !text-white hover:!border-bun hover:!text-bun">
                Lihat Tempat Beli
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
